/**
 * Core Module
 *
 * Singleton services shared across the entire application:
 *  - AuthenticationService (Angular 18) — registered via @Injectable, providedIn: 'root'
 *  - PermissionsService (Angular 18) — registered via @Injectable, providedIn: 'root'
 *  - CsrfInterceptor — attaches CSRF token to mutating HTTP requests
 *  - Bridges to AngularJS services that have not been migrated yet:
 *      ROOT_SCOPE_PORT, MD5_PORT (used by PermissionsService)
 *      STATE_PORT (ui-router $state, used by RouteParamsService and
 *      FeatureCustomersService)
 *
 * Import this module ONLY in AppModule.
 */
import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';

import { CsrfInterceptor } from './auth/csrf.interceptor';
import { USER_PREFERENCES_PORT } from './calendar/user-preferences.port';
import { MD5_PORT } from './permissions/md5.port';
import {
  FILTER_PORT,
  LOCATION_PORT,
  Q_PORT,
  ROOT_SCOPE_PORT,
  STATE_PORT,
  TIMEOUT_PORT,
  WINDOW_PORT,
} from './utils';
import { THEMES_HELPER_PORT } from './validate/themes-helper.port';
import { UIB_MODAL_PORT } from './modal/uib-modal.port';
import { GROWL_PORT } from './notification/growl.port';

type AjsInjector = { get: (name: string) => unknown };

@NgModule({
  imports: [CommonModule, HttpClientModule],
  providers: [
    // CSRF interceptor — replaces legacy $http.defaults.headers
    { provide: HTTP_INTERCEPTORS, useExisting: CsrfInterceptor, multi: true },

    // Bridges to AngularJS services (resolved lazily via $injector)
    {
      provide: ROOT_SCOPE_PORT,
      useFactory: (i: AjsInjector) => i.get('$rootScope'),
      deps: ['$injector'],
    },
    {
      provide: MD5_PORT,
      useFactory: (i: AjsInjector) => i.get('md5'),
      deps: ['$injector'],
    },
    {
      provide: TIMEOUT_PORT,
      useFactory: (i: AjsInjector) => i.get('$timeout'),
      deps: ['$injector'],
    },
    {
      provide: Q_PORT,
      useFactory: (i: AjsInjector) => i.get('$q'),
      deps: ['$injector'],
    },
    {
      provide: FILTER_PORT,
      useFactory: (i: AjsInjector) => i.get('$filter'),
      deps: ['$injector'],
    },
    {
      provide: LOCATION_PORT,
      useFactory: (i: AjsInjector) => i.get('$location'),
      deps: ['$injector'],
    },
    {
      provide: WINDOW_PORT,
      useFactory: (i: AjsInjector) => i.get('$window'),
      deps: ['$injector'],
    },
    {
      provide: USER_PREFERENCES_PORT,
      useFactory: (i: AjsInjector) => i.get('UserPreferencesService'),
      deps: ['$injector'],
    },
    {
      provide: THEMES_HELPER_PORT,
      useFactory: (i: AjsInjector) => i.get('ThemesHelperService'),
      deps: ['$injector'],
    },
    // $uibModal (ui.bootstrap) bridge — used by ModalService (Wave 3 item #27).
    // Stays for the lifetime of the AngularJS shell ($uibModal has no Angular 18
    // equivalent in this hybrid app).
    {
      provide: UIB_MODAL_PORT,
      useFactory: (i: AjsInjector) => i.get('$uibModal'),
      deps: ['$injector'],
    },
    // UI-Router $state bridge — used by FeatureCustomersService (Wave 3 item #26)
    // and RouteParamsService (Zendesk SSO `return_to`, DEV-11862).
    // Stays for the lifetime of the AngularJS shell (UI-Router has no Angular 18
    // equivalent in this hybrid app).
    {
      provide: STATE_PORT,
      useFactory: (i: AjsInjector) => i.get('$state'),
      deps: ['$injector'],
    },
    // `growl` (angular-growl-v2) bridge — used by NotificationService. Stays for
    // the lifetime of the AngularJS shell, or until the growl backend is
    // replaced by a native toast (see NotificationService).
    {
      provide: GROWL_PORT,
      useFactory: (i: AjsInjector) => i.get('growl'),
      deps: ['$injector'],
    },
  ],
})
export class CoreModule {}
